import React from "react";
import { clearLoginSession, UnauthenticatedError } from "../../utils/auth";

const SENSITIVE_PARAMS = ["token", "access", "refresh", "code", "auth"];
const JWT_REGEX = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

export function sanitizeUrl(input) {
    try {
        const url = new URL(String(input));
        SENSITIVE_PARAMS.forEach((p) => {
            if (url.searchParams.has(p)) url.searchParams.set(p, "<redacted>");
        });
        const result = (url.pathname + url.search)
            .replace(/%3Credacted%3E/gi, "<redacted>")
            .replace(JWT_REGEX, "<redacted>");
        return result.slice(0, 200);
    } catch {
        return String(input).replace(JWT_REGEX, "<redacted>").slice(0, 200);
    }
}

export function sanitizeMessage(input) {
    return String(input == null ? "" : input).replace(JWT_REGEX, "<redacted>").slice(0, 200);
}

/**
 * ErrorBoundary - React render/lifecycle 에러를 포착하여 화이트스크린을 차단한다.
 * (CROSS-001) 단일 top-level 사용. inner ErrorBoundary는 후속 phase.
 *
 * Props:
 * - children: 보호 대상 트리
 *
 * Note:
 * - 우선순위: auth > chunk > generic
 * - UnauthenticatedError는 /login redirect (sessionStorage TTL 3s loop 가드)
 * - chunk load error는 자동 새로고침 1회 시도 (lazy import 회복 패턴)
 * - generic error는 Discord webhook 발신 (5분 dedupe)
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
        this.didReloadOnce = false;
        this.didAuthRedirect = false;
    }

    static getDerivedStateFromError(error) {
        // UnauthenticatedError는 componentDidCatch에서 redirect 처리 → state 변경 없음
        if (error && error.name === "UnauthenticatedError") {
            return null;
        }
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // (1) Auth 분기 — UnauthenticatedError 최우선
        if ((error && error.name === "UnauthenticatedError") || error instanceof UnauthenticatedError) {
            this.handleAuthError();
            return;
        }

        // (2) Chunk load error는 캐시 무효화 가능성이 높으므로 1회 자동 새로고침
        const message = error && error.message ? String(error.message) : "";
        const isChunkError =
            message.includes("ChunkLoadError") ||
            message.includes("Loading chunk") ||
            message.includes("Loading CSS chunk");

        if (isChunkError && !this.didReloadOnce) {
            this.didReloadOnce = true;
            window.location.reload();
            return;
        }

        // (3) Generic 로깅 + Discord webhook (자산 보호 observability)
        if (process.env.NODE_ENV !== 'production') {
            console.error("[ErrorBoundary]", error, errorInfo);
        }
        this.sendDiscordAlert("ErrorBoundary caught", error);
    }

    handleAuthError = () => {
        // sessionStorage TTL 3초 가드 (infinite redirect loop 차단)
        const KEY = "auth_redirect_at";
        const last = sessionStorage.getItem(KEY);
        if (last && Date.now() - Number(last) < 3000) {
            // 3초 내 재진입 → fallback UI 노출 (무한 루프 방지)
            this.setState({ hasError: true, error: new Error("Auth redirect loop detected") });
            return;
        }
        sessionStorage.setItem(KEY, String(Date.now()));

        try { clearLoginSession(); } catch (e) {}
        this.didAuthRedirect = true;
        window.location.replace("/login");
    };

    sendDiscordAlert = (title, error) => {
        const WEBHOOK = "https://discord.com/api/webhooks/1475113982279946290/hDX1tTI6muP0hIrZkOtP6Oxak-PzceUqCzCHu4z2fqNHzuu05FjiMyX9GRZN_ZtSoDAh";

        // 5분 dedupe (동일 error.name 스팸 차단)
        const DEDUPE_KEY = "eb_alert_" + (error && error.name ? error.name : "unknown");
        const last = sessionStorage.getItem(DEDUPE_KEY);
        if (last && Date.now() - Number(last) < 5 * 60 * 1000) return;
        sessionStorage.setItem(DEDUPE_KEY, String(Date.now()));

        try {
            fetch(WEBHOOK, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    embeds: [{
                        title: "🚨 Dashboard 프론트엔드 에러 — " + title,
                        fields: [
                            // error.name도 sanitizeMessage 경유 — 커스텀 Error에서 name을 임의 값으로 설정하는 경우 방어 (DAS-63 M2 fast-follow)
                            { name: "에러 이름", value: sanitizeMessage(error && error.name ? error.name : "Unknown"), inline: true },
                            { name: "메시지", value: sanitizeMessage(error && error.message ? error.message : "") },
                            { name: "URL", value: sanitizeUrl(window.location.href), inline: true },
                            { name: "시간", value: new Date().toISOString(), inline: true }
                        ],
                        color: 15158332
                    }]
                })
            }).catch(() => {});
        } catch (e) {}
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.didAuthRedirect) {
            // redirect 중 fallback UI 깜빡임 방지
            return null;
        }
        if (!this.state.hasError) {
            return this.props.children;
        }

        return (
            <div
                role="alert"
                className="min-h-screen flex items-center justify-center bg-surface px-4 py-8"
            >
                <div className="max-w-md w-full bg-surface-card border border-surface-border rounded-2xl shadow-card p-6 sm:p-8">
                    <h1 className="text-content text-lg sm:text-xl font-semibold mb-2">
                        문제가 발생했습니다
                    </h1>
                    <p className="text-content-secondary text-sm leading-relaxed mb-6">
                        페이지 렌더링 중 오류가 발생했습니다. 새로고침을 시도해주세요.
                    </p>
                    <button
                        type="button"
                        onClick={this.handleReload}
                        className="w-full bg-brand text-content-inverse hover:bg-brand-light transition-colors rounded-lg px-4 py-2.5 text-sm font-medium min-h-[44px]"
                    >
                        새로고침
                    </button>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
